import { useEffect } from "react";
import useFetch from "hooks/useFetch";
import mixpanel from "mixpanel-browser";
import { useSetRecoilState } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import { setAuthHeaders } from "api";
import { userAtom } from "recoil/userAtom";
import { useNavigate } from "react-router-dom";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "./useIsInappWebview";
import { loginBlockModalAtom } from "recoil/loginBlockModalAtom";
import {
  syncFbcToAccount,
  trackCapiStandardEvent,
} from "../analytics/metaCapi";
import {
  getPartnerLoginField,
  clearPartnerLoginField,
} from "utils/partnerAuth";

// 로그인 후 복귀 경로(파트너 결제 등). 설정돼 있으면 "/" 대신 이 경로로 이동.
export const POST_LOGIN_RETURN_URL_KEY = "postLoginReturnUrl";

export default function useLogin({
  url,
  idToken,
  code,
  accessToken,
  refreshToken,
  type,
  redirectUri,
}: {
  url: string;
  idToken?: string;
  accessToken?: string;
  refreshToken?: string;
  code?: string;
  type: "google" | "apple" | "kakao";
  redirectUri?: string;
}) {
  let navigate = useNavigate();

  // 파트너 결제에서 진입한 로그인이면 백엔드에 partner 식별자 전달 (그 외엔 미포함)
  const partner = getPartnerLoginField();
  const [loginResult, login] = useFetch({
    url: url,
    requestBody: JSON.stringify({
      type: type,
      ...(idToken && { idToken }),
      ...(code && { code }),
      ...(accessToken && { accessToken }),
      ...(refreshToken && { refreshToken }),
      ...(redirectUri && { redirectUri }),
      ...(partner && { partner }),
    }),
  });

  const setPatientState = useSetRecoilState(patientAtom);
  const setUser = useSetRecoilState(userAtom);
  const setLoginBlockModal = useSetRecoilState(loginBlockModalAtom);
  const isInappWebview = useIsInappWebview();

  useEffect(() => {
    if (loginResult) {
      const result = loginResult.result;

      if (isInappWebview && result.role !== "patient") {
        setLoginBlockModal({
          open: true,
          title: "로그인 실패",
          subtitle: "코치는 PC에서 로그인해주세요.",
        });
        return;
      }

      mixpanel.identify(result.trackId);

      setAuthHeaders(result.accessToken);
      sendMessage(
        JSON.stringify({
          event: "value",
          params: {
            accessToken: result.accessToken,
          },
        })
      );

      setUser(result);
      if (result.role === "patient" && result.userId) {
        setPatientState({
          patientId: Number(`${result.userId}`),
          nickname: result.nickname,
        });
      }

      // 브라우저 재로그인 등 CAPI 이벤트가 없는 로그인에서도 서버 저장 fbc를 최신 클릭으로 동기화
      // (인앱 웹뷰는 fbc 쿠키가 없어 자동 no-op)
      syncFbcToAccount(result.userId);

      if (isInappWebview && result.isFirstLogin && result.isInsider !== true) {
        sendMessage(
          JSON.stringify({
            event: "send_fb_event",
            params: {
              eventName: "Login",
            },
          })
        );

        trackCapiStandardEvent(
          "CompleteRegistration",
          {
            value: 0,
            currency: "KRW",
            externalId: result.userId,
            email: result.email,
          },
          undefined,
          undefined,
          true
        );

        sendMessage(
          JSON.stringify({
            event: "send_firebase_analytics_event",
            params: {
              eventName: "sign_up",
              eventParams: {
                method: type,
              },
            },
          })
        );
      }

      clearPartnerLoginField();
      const returnUrl = sessionStorage.getItem(POST_LOGIN_RETURN_URL_KEY);
      if (returnUrl) sessionStorage.removeItem(POST_LOGIN_RETURN_URL_KEY);
      navigate(returnUrl && returnUrl.startsWith("/") ? returnUrl : "/", {
        replace: true,
      });
    }
  }, [loginResult]);

  return login;
}
